import { Injectable } from '@angular/core';
import {
  HttpClient,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  StudentListing,
  CampusWard,
  CampusWardDetails,
  LocalWard,
  HomeWardWithStudents,
  WardLeader,
  WardGroupAssignments,
  WardGroup,
  WardGroupType,
  MemberInfo,
  HomeWardWithCheckAbleStudents,
  CampusWardDetailsForUpdate,
  CampusUnitAccess,
  UnitLoginResult
} from '../../models';
import { AbstractDataService } from './abstract-data-service';
import { catchError, retry } from 'rxjs/operators';

@Injectable()
export class CampusWardsDataService extends AbstractDataService {
  private baseUrl = 'api/campus-units/wards';

  constructor(private http: HttpClient) {
    super();
  }

  /**
   * Gets the BYU-Idaho wards for single and married students.
   */
  getCampusWards(): Observable<CampusWard[]> {
    return this.http.get<CampusWard[]>(`${this.baseUrl}`)
    .pipe(
      retry(2)
    );
  }

  /**
   * Gets the detailed information for the campus ward.
   */
  getCampusWardDetails(campusWardId: number): Observable<CampusWardDetails> {
    return this.http
      .get<CampusWardDetails>(`${this.baseUrl}/${campusWardId}`)
      .pipe(catchError(this.handleNotFound));
  }

  /**
   * Gets the detailed information for the campus ward.
   */
   getCampusWardDetailsForUpdate(campusWardId: number): Observable<CampusWardDetailsForUpdate> {
    return this.http
      .get<CampusWardDetailsForUpdate>(`${this.baseUrl}/for-update/${campusWardId}`)
      .pipe(catchError(this.handleNotFound));
  }

  /**
   * Gets the local wards that have been assigned to the campus ward.
   */
  getAssignedLocalWards(campusWardId: number): Observable<LocalWard[]> {
    return this.http.get<LocalWard[]>(
      `${this.baseUrl}/${campusWardId}/local-wards`
    );
  }

  /**
   * Update a campus ward's information, including meetings.
   */
  updateWardInformation(campusWardDetails: CampusWardDetailsForUpdate): Observable<CampusWardDetailsForUpdate> {
    return this.http
      .put<CampusWardDetailsForUpdate>(
        `${this.baseUrl}/${campusWardDetails.campusWardId}/information`,
        campusWardDetails
      )
      .pipe(
        catchError(this.handleBadRequest('Unable to update ward information.'))
      );
  }

  /**
   * Update a campus ward.  This for when the admin is updating it.
   */
  updateCampusWard(
    campusWardDetails: CampusWardDetailsForUpdate
  ): Observable<CampusWardDetailsForUpdate> {
    return this.http
      .put<CampusWardDetailsForUpdate>(
        `${this.baseUrl}/${campusWardDetails.campusWardId}`,
        campusWardDetails
      )
      .pipe(
        catchError(this.handleBadRequest('Unable to update ward information.'))
      );
  }

  /**
   * Create a new campus ward.
   */
  createCampusWard(
    newCampusWardDetails: CampusWardDetailsForUpdate
  ): Observable<CampusWardDetailsForUpdate> {
    return this.http
      .post<CampusWardDetailsForUpdate>(`${this.baseUrl}`, newCampusWardDetails)
      .pipe(catchError(this.handleBadRequest('Unable to create new ward.')));
  }

  /**
   * Deletes, to the user at least, (really inactivates) a campus ward.
   */
  deleteCampusWard(campusWardId: number) {
    return this.http.delete(`${this.baseUrl}/${campusWardId}`);
  }

  /**
   * Creates the ward leader's information, including the photograph.
   *
   * @param leader The leader's information
   * @param photo The leader's new photo, if any
   */
  createWardLeader(leader: WardLeader, photo: File): Observable<WardLeader> {
    const formData = this.getLeaderFormData(Object.assign({}, leader), photo);
    return this.http.post<WardLeader>(`${this.baseUrl}/${leader.campusWardId}/leaders`, formData)
      .pipe(
        catchError(
          this.handleBadRequest('Unable to update ward leader information.')
        )
      );
  }

  /**
   * Updates the ward leader's information, including the photograph.
   *
   * @param leader The leader's information
   * @param photo The leader's new photo, if any
   * @param removePhoto A flag letting us know if we want to remove the leader's current photo or not. This will trump the new photo.
   */
  updateWardLeader(leader: WardLeader, photo: File, removePhoto: boolean): Observable<WardLeader> {
    const updateModel = Object.assign({ deleteExistingPhoto: false }, leader);
    updateModel.photoBase64 = null; // clear this out to just reduce data going over the wire

    if (removePhoto) {
      updateModel.deleteExistingPhoto = true;
    }

    const formData = this.getLeaderFormData(updateModel, photo);

    return this.http.put<WardLeader>(`${this.baseUrl}/${leader.campusWardId}/leaders/${
          leader.campusWardOfficeId
        }`,
        formData
      )
      .pipe(
        catchError(
          this.handleBadRequest('Unable to update ward leader information.')
        )
      );
  }

  /**
   * Deletes the ward leader's information.
   */
  deleteWardLeader(leader: WardLeader) {
    return this.http.delete(
      `${this.baseUrl}/${leader.campusWardId}/leaders/${
        leader.campusWardOfficeId
      }`
    );
  }

  /**
   * Get students attending to ward assigned to a campus ward.
   */
  getPossibleLocalStudents(
    campusWardId: number
  ): Observable<HomeWardWithStudents[]> {
    return this.http.get<HomeWardWithStudents[]>(
      `${this.baseUrl}/${campusWardId}/possible-local-students`
    );
  }

  /**
   * Get students attending to ward assigned to a campus ward.
   */
  getPossibleCheckableLocalStudents(
    campusWardId: number
  ): Observable<HomeWardWithCheckAbleStudents[]> {
    return this.http.get<HomeWardWithCheckAbleStudents[]>(
      `${this.baseUrl}/${campusWardId}/possible-local-students`
    );
  }

  /**
   * Gets the local students assigned/attending the ward.
   */
  getLocalStudents(
    campusWardId: number,
    groupTypeId: number
  ): Observable<StudentListing[]> {
    return this.http.get<StudentListing[]>(
      `${
        this.baseUrl
      }/${campusWardId}/local-students?campusWardGroupTypeId=${groupTypeId}`
    );
  }

  /**
   * Updates students campus ward information
   *
   * @param students The students being updated
   * @param campusWard The ward we're updating for
   * @param remove Whether or not we're removing assignments
   */
  updateStudentsCampusWard(
    students: StudentListing[],
    campusWard: CampusWard,
    remove: boolean
  ) {
    const info = {
      campusWardUnitNumber: campusWard.unitNumber,
      students: students,
      removeAssignments: remove
    };
    return this.http.post(
      `${this.baseUrl}/${campusWard.campusWardId}/local-students`,
      info
    );
  }

  /**
   * Gets the valid ward group types.
   */
  getWardGroupTypes(): Observable<WardGroupType[]> {
    return this.http
      .get<WardGroupType[]>(`api/campus-units/ward-group-types`)
      .pipe(
        retry(2)
      );
  }

  /**
   * Gets the ward's active groups.
   */
  getWardGroups(campusWardId: number): Observable<WardGroup[]> {
    return this.http.get<WardGroup[]>(`${this.baseUrl}/${campusWardId}/groups`);
  }

  /**
   * Gets the ward's active groups.
   */
  getWardGroupsOfType(
    campusWardId: number,
    groupTypeId: number
  ): Observable<WardGroup[]> {
    return this.http.get<WardGroup[]>(
      `${
        this.baseUrl
      }/${campusWardId}/groups?campusWardGroupTypeId=${groupTypeId}&activeOnly=true`
    );
  }

  /**
   * Creates the ward group.
   */
  createWardGroup(group: WardGroup): Observable<WardGroup> {
    return this.http.post<WardGroup>(`${this.baseUrl}/${group.campusWardId}/groups`, group)
      .pipe(catchError(this.handleBadRequest('Unable to create group.')));
  }

  /**
   * Updates stake info
   */
  updateWardGroup(group: WardGroup) {
    return this.http
      .put(
        `${this.baseUrl}/${group.campusWardId}/groups/${
          group.campusWardGroupId
        }`,
        group
      )
      .pipe(
        catchError(this.handleBadRequest('Unable to update group information.'))
      );
  }

  /**
   * Gets the ward's group assignments for the given semester and group type.
   */
  getWardGroupAssignments(
    campusWardId: number,
    groupTypeId: number,
    year: number,
    session: string
  ): Observable<WardGroupAssignments> {
    const url = `${
      this.baseUrl
    }/${campusWardId}/groups/assignments?campusWardGroupTypeId=${groupTypeId}&year=${year}&session=${session}`;
    return this.http.get<WardGroupAssignments>(url);
  }

  /**
   * Updates the ward's group assignments for the given semester and group type.
   */
  rollWardGroupAssignments(
    campusWardId: number,
    groupTypeId: number,
    year: number,
    session: string
  ): Observable<WardGroupAssignments> {
    const url = `${this.baseUrl}/${campusWardId}/groups/rolled-assignments`;
    const information = {
      campusWardId: campusWardId,
      campusWardGroupTypeId: groupTypeId,
      session: session,
      year: year
    };

    return this.http.post<WardGroupAssignments>(url, information);
  }

  /**
   * Updates the ward's group assignments for the given semester and group type.
   */
  updateWardGroupAssignments(
    assignments: WardGroupAssignments
  ): Observable<WardGroupAssignments> {
    const url = `${this.baseUrl}/${
      assignments.campusWardId
    }/groups/assignments`;
    return this.http.put<WardGroupAssignments>(url, assignments);
  }

  /**
   * Removes the ward group assignments.
   */
  removeWardGroupAssignments(
    campusWardId: number,
    campusWardGroupAssignmentIds: number[]
  ) {
    return this.http.post(
      `${this.baseUrl}/${campusWardId}/groups/assignment-removals`,
      campusWardGroupAssignmentIds
    );
  }

  /**
   * Gets the form data to use to create or update a leader's information.
   *
   * @param model The model to send as json with the leader's information
   * @param photo The leader's photo
   */
  private getLeaderFormData(model: any, photo: File): FormData {
    const formData = new FormData();
    formData.append('leaderInformationJson', JSON.stringify(model));
    if (photo) {
      formData.append('newPhoto', photo, photo.name);
    }

    return formData;
  }

  /**
   * Gets campus ward directory
   *
   * @param campusWardId
   * @param term
   * @param wardDirectoryParams
   */
  getCampusWardDirectory(
    campusWardId: number,
    term: string,
    year: number,
    excludeNonAttendingLocalStudents = false,
    loadOnlyBasicData = false
  ): Observable<MemberInfo[]> {
    return this.http.get<MemberInfo[]>(
      `${this.baseUrl}/${campusWardId}/term/${term}/year/${year}/WardDirectory?excludeNonAttendingLocalStudents=${excludeNonAttendingLocalStudents}&loadOnlyBasicData=${loadOnlyBasicData}`
    );
  }

   /**
   * Gets the current campus ward directory
   * @param campusWardId
   */
  getCurrentCampusWardDirectory(
    campusWardId: number,
  ): Observable<MemberInfo[]> {
    return this.http.get<MemberInfo[]>(
      `${this.baseUrl}/${campusWardId}/dir/current`
    );
  }

  /**
   * Gets the current campus ward directory
   * with only mimimal student information
   * @param campusWardId
   */
  getCurrentBasicCampusWardDirectory(
    campusWardId: number,
  ): Observable<MemberInfo[]> {
    return this.http.get<MemberInfo[]>(
      `${this.baseUrl}/${campusWardId}/dir/current/basic`
    );
  }

  /**
   * Ward leader login
   *
   * @param campusWard
   */
  unitLogIn(campusWard: CampusUnitAccess) {
    return this.http
      .post<UnitLoginResult>(`${this.baseUrl}/logIn`, campusWard)
      .pipe(catchError(this.handleBadRequest('Leader not authorized.')));
  }

  /**
   * Gets the BYU-Idaho wards for single and married students.
   */
  getCampusWardById(wardId: number): Observable<CampusWard> {
    return this.http.get<CampusWard>(`${this.baseUrl}/${wardId}`)
    .pipe(
      retry(2)
    );
  }
}
